import 'swiper/css';
import 'swiper/css/navigation';

import { CloseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { Skeleton } from 'antd';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Flex } from '@/components/base';
import { Stage } from '@/components/Stage/Stage';
import useColor from '@/hooks/domain/useColor';
import LoadingLayout from '@/layouts/LoadingLayout';
import service from '@/services/index';

type Props = AttachmentT & {
  onClick: () => void;
  onDeleteAttachment?: (alias: string) => void;
};

const AttachmentItem = (props: Props) => {
  const color = useColor();
  const { alias, category, onClick, isLoading, onDeleteAttachment } = props;
  const imgRef = useRef<HTMLImageElement>(null);
  const [url, setUrl] = useState<string>('');
  const [imageLoading, setImageLoading] = useState<boolean>(true);

  useEffect(() => {
    setImageLoading(true);
    service.attachment
      .getAttachment(alias, 'thumb')
      .then((data) => {
        const fileUrl = URL.createObjectURL(data);
        setUrl(fileUrl);
        if (imgRef.current) {
          imgRef.current.src = fileUrl;
        }
        setImageLoading(false);
      })
      .catch(() => {
        setImageLoading(false);
      });

    // Cleanup function는 다음 effect 실행 시 또는 컴포넌트 언마운트 시 호출됨
    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [alias]);

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
  };

  const [hover, setHover] = useState(false);
  return (
    <Wrapper
      style={{ zIndex: 8600 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {isLoading ? (
        <LoadingLayout style={{ width: '100%', height: '100%' }}>
          Uploading...
        </LoadingLayout>
      ) : (
        <>
          {category === 1 && (
            <Stage>
              <Stage.Scene status={imageLoading}>
                <Skeleton.Node active style={{ width: 156, height: 156 }} />
              </Stage.Scene>
              <Stage.Scene status={!imageLoading}>
                <img
                  src={url}
                  ref={imgRef}
                  alt="thumbnail"
                  className="thumbnail"
                  onClick={onClick}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              </Stage.Scene>
            </Stage>
          )}
          {category !== 1 && (
            <Flex className="overlay" j="center" a="center">
              <PlayCircleOutlined
                style={{ fontSize: '36px', color: color.gray300 }}
              />
            </Flex>
          )}
          {onDeleteAttachment && hover && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                cursor: 'pointer',
              }}
              onClick={() => onDeleteAttachment(alias)}
            >
              <CloseCircleOutlined
                style={{ backgroundColor: color.gray100, borderRadius: 14 }}
              />
            </div>
          )}
        </>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  width: 156px;
  height: 156px;
  border-radius: 16px;
  overflow: hidden;
  box-sizing: border-box;

  .thumbnail {
    object-fit: cover;
    height: 156px;
    width: 156px;
    border-bottom: 1px solid ${(props) => props.theme.colors.gray400};
    cursor: pointer;
  }

  .overlay {
    width: 156px;
    height: 84px;
    background: ${(props) => props.theme.colors.ND20};
    position: absolute;
    top: 0px;
    left: 0px;
  }
`;

export default AttachmentItem;
